import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { createMarkerHashedKey } from 'libraries/mapLibrary';
import { BasicCommunitySummaryCard,
  PaidCommunitySummaryCard, PaidMasterPlannedCommunitySummaryCard } from 'features/summaryCard';
import MarkerType from '../enums/markerType';
import { isHousingDevelopmentViewed } from '../services/markerSessionService';
import getClassesToApply from '../services/unitMarkerService';
import { markerTipSvgHtml } from '../constants/svgIcons';

/* Creates a new instance of a housing unit that will be plotted in the map as a marker. */
export default class BuildablePlanMarker {
  constructor(housingInformation) {
    this.geoId = housingInformation.GeoId;
    this.detailText = housingInformation.DetailText;
    this.type = MarkerType.BuildablePlan.toString();
    this.id = housingInformation.Id;
    this.communityId = housingInformation.Id;
    this.latitude = housingInformation.Latitude;
    this.longitude = housingInformation.Longitude;
    this.priceReducedText = housingInformation.PriceReducedText;
    // TODO: NewHomeSource Rebrand
    // Change the class on the backend.
    this.priceText = housingInformation.PriceText.replace(
      'small--text',
      'nhs-c-tooltip__price-label--small'
    );
    this.shortPrice = housingInformation.ShortPrice;
    this.title = housingInformation.Title;
    this.totalPlans = +housingInformation.TotalPlans;
    this.previousPrice = housingInformation.PreviousPrice;
    this.isHotDeal = housingInformation.IsHotDeal;
    this.priceReducedTrackingLabel =
      housingInformation.PriceReducedTrackingLabel;
    this.zIndex = 2;
    this.builderId = housingInformation.BuilderId;
    this.isViewed = isHousingDevelopmentViewed(housingInformation.Id);
    this.priceLow = housingInformation.PriceLow;
    this.priceHigh = housingInformation.PriceHigh;
    this.hasInteractiveSitePlan = housingInformation.HasInteractiveSitePlan;
    this.sitePlanCenterLatitude = housingInformation.SitePlanCenterLatitude;
    this.sitePlanCenterLongitude = housingInformation.SitePlanCenterLongitude;
    this.isBasic = housingInformation.IsBasic || housingInformation.IsBl;
    this.isCallForPrice = housingInformation.IsCallForPrice || false;
    this.wasMatchedAsSpotlightResult = housingInformation.WasMatchedAsSpotlightResult || false;
    this.thumbnail = housingInformation.Thumbnail;
    this.bedrooms = housingInformation.Bedrooms;
    this.bathrooms = housingInformation.Bathrooms;
    this.address = housingInformation.Address;
    this.brandName = housingInformation.BrandName;
    this.squareFeet = housingInformation.SquareFeetRange;
    this.communityStatus = housingInformation.CommunityStatus;
    this.averageRating = housingInformation.AverageRating;
    this.isAdult = housingInformation.IsAdult;
    this.stateAbbreviation = housingInformation.StateAbbreviation;
    this.city = housingInformation.City;
    this.zip = housingInformation.Zip;

    if (this.isBasic) {
      this.zIndex = 1;
    } else if (this.wasMatchedAsSpotlightResult) {
      this.zIndex = 5;
    } else {
      this.zIndex = 4;
    }
  }

  /**
   * @returns The name of the extend marker type.
   */
  getType() {
    return this.type;
  }

  /**
   * getMarkerText: gets the text the marker is going to show once rendered.
   * @returns a text to show on the map once rendered.
   */
  getMarkerText() {
    return `<span data-qa="total_plans_marker">${this.totalPlans}</span><span>Plans</span>`;
  }

  /**
   * This function returns a string that is the url to the detail page for the community.
   * @returns The string `/communitydetail/builder-${this.builderId}/community-${this.id}`
   */
  getDetailUrl() {
    return `/communitydetail/builder-${this.builderId}/community-${this.id}`;
  }

  /**
   * This function returns a new GoogleAnalyticsEvent object with the event category, event action, and
   * event label set.
   * @returns A new instance of the GoogleAnalyticsEvent class.
   */
  getGoogleAnalyticsEventForClicks() {
    const customDimensions = {
      [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]:
        this.priceReducedTrackingLabel
    };

    const url = this.getDetailUrl();
    return new GoogleAnalyticsEvent(
      'Map-Pin-Clicks',
      'Homes: SRP-Map-Home-Clicks',
      url,
      customDimensions
    );
  }

  /**
   * This function returns a new meta data object with the required information based on the map pin data.
   * @returns A new instance of the meta data object.
   */
  getHousingLocationMetaData() {
    return {
      priceReducedTrackingLabel: this.priceReducedTrackingLabel,
      communityName: this.title,
      communityId: this.id,
      isPaid: !this.isBasic,
      priceLow: this.priceLow,
      priceHigh: this.priceHigh,
      hasInteractiveSitePlan: this.hasInteractiveSitePlan,
      id: this.id,
      thumbnail: this.thumbnail,
      priceText: this.priceText,
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      address: this.address,
      brandName: this.brandName,
      squareFeet: this.squareFeet,
      url: this.getDetailUrl(),
      builderId: this.builderId,
      wasMatchedAsSpotlightResult: this.wasMatchedAsSpotlightResult,
      sitePlanCenterLatitude: this.sitePlanCenterLatitude,
      sitePlanCenterLongitude: this.sitePlanCenterLongitude,
      communityStatus: this.communityStatus,
      averageRating: this.averageRating,
      previousPrice: this.previousPrice,
      isHotDeal: this.isHotDeal,
      isAdult: this.isAdult,
      stateAbbreviation: this.stateAbbreviation,
      city: this.city,
      zip: this.zip,
    };
  }

  /**
   * This function returns the HTML in charge of build the hover card of a given marker
   * @returns hover card HTML
   */
    getCard() {
      if (this.isMasterPlannedCommunity) {
        return new PaidMasterPlannedCommunitySummaryCard();
      }

      if (this.isBasic) {
        return new BasicCommunitySummaryCard();
      }

      return new PaidCommunitySummaryCard();
    }

  /**
   * @returns {string} The complete HTML that represents the buildable plan marker as an icon and a card with details.
   */
  getIcon() {
    const card = this.getCard();
    card.setInformation({
      id: this.id,
      name: this.title,
      url: this.getDetailUrl(),
      price: this.priceText,
      priceLow: this.priceLow,
      priceHigh: this.priceHigh,
      priceReduced: this.priceReducedText,
      wasMatchedAsSpotlightResult: this.wasMatchedAsSpotlightResult,
      isLimited: !!this.isCallForPrice,
      thumbnail: this.thumbnail,
      latitude: this.latitude,
      longitude: this.longitude,
      zip: this.zip,
      city: this.city,
      stateAbbreviation: this.stateAbbreviation,
      builderId: this.builderId,
      brandName: this.brandName,
      builderRating: this.averageRating,
      formattedBedrooms: this.bedrooms,
      formattedBathrooms: this.bathrooms,
      formattedGarages: this.garages,
      formattedSquareFeet: this.squareFeet,
      sitePlanCenterLatitude: this.sitePlanCenterLatitude,
      sitePlanCenterLongitude: this.sitePlanCenterLongitude,
      marketingStatus: this.communityStatus,
      isAdult: this.isAdult,
      communityCount: this.communityCount,
      builderCount: this.builderCount
    });

    return `
      <div data-marker-content class="nhs-c-advance-marker nhs-c-tooltip-hide">
        ${this.getHtmlPinElement()}
        ${card.getHtml()}
      </div>
    `;
  }

  /**
   * getDescription: returns a description that will help screen readers
   * provide a better explanation about the marker itself.
   * @returns a description for the marker.
   */
  getDescription() {
    if (this.shortPrice) {
      return `Plans from ${this.shortPrice}`;
    }

    return this.title;
  }

  /**
   * getMarkerTextNode: gets the html node to represent the marker text
   * @returns a string representing the html node to render a marker text
   */
  getMarkerTextNode() {
    if (this.getMarkerText()) {
      return `<div data-marker-text data-qa="marker_price" class="nhs-c-marker__text">
                ${this.getMarkerText()}
              </div>`;
    }

    return '';
  }

  /**
   * getMarkerStyles function is in charge of returning an array with the classes to use for the map marker.
   * @returns an array representing all the classes we should apply to the map marker in terms of its characteristics.
   */
  getMarkerStyles() {
    const { base } = getClassesToApply({
      isBasic: this.isBasic,
      wasMatchedAsSpotlightResult: this.wasMatchedAsSpotlightResult,
      isViewed: this.isViewed
    });

    const classesToApply = [...base].filter(Boolean);
    return classesToApply;
  }

  /**
   * Creates an HTML string representing a pin marker of the buildable plan.
   * For basic markers, returns a minimal div without arrows or inner content.
   * For all others, includes marker text and arrow elements.
   * @returns {string} The HTML string for the marker pin.
   */
  getHtmlPinElement() {
    const markerStyles = this.getMarkerStyles().join(' ');
    const dataAttributes = [
      `data-marker-icon="true"`,
      `class="${markerStyles}"`,
      `data-id="${this.id}"`,
      `data-type="${this.getType()}"`,
      `data-geographic-key="${createMarkerHashedKey(
        this.latitude,
        this.longitude
      )}"`,
      `data-price-reduced-tracking-label="${this.priceReducedTrackingLabel}"`,
      `data-was-matched-as-spotlight-result="${this.wasMatchedAsSpotlightResult}"`,
      `data-is-no-price="${this.isCallForPrice}"`,
      `data-viewed="${this.isViewed}"`,
      `data-is-price-reduction="${!!this.previousPrice}"`,
      `data-qa="marker_container"`,
      this.isBasic ? `data-is-basic="true"` : `data-is-paid="true"`
    ].join(' ');

    if (!this.shortPrice || this.isCallForPrice) {
      return `<div ${dataAttributes}></div>`;
    }

    return `
      <div ${dataAttributes}>        
        <div class="nhs-c-marker nhs-c-marker__plan">
          <div class="nhs-c-marker__box">
            ${this.getMarkerTextNode()}
            <div class="nhs-c-marker__arrow--tip">${markerTipSvgHtml}</div>
          </div>
        </div>
      </div>
    `;
  }
}
